import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initVirtualBackground } from 'features/virtual-backgrounds/thunks/initVirtualBackground';
import {
  SelectedVirtualBackground,
  VirtualBackgroundActivationFailure,
  VirtualBackgroundItem,
  VirtualBackgroundsState,
  VirtualBackgroundSyncConfig,
} from 'features/virtual-backgrounds/types';
import { SDKConnected } from 'features/sdk/sdkStateSlice';
import { defaultVirtualBackgrounds } from 'features/virtual-backgrounds/default-backgrounds';
import { roomValidated } from 'features/room/roomSlice';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { mapPersistedVirtualBackgrounds } from 'features/virtual-backgrounds/utils/mapPersistedVirtualBackgrounds';
import { mergeVirtualBackgrounds } from 'features/virtual-backgrounds/utils/mergeVirtualBackgrounds';
import {
  signalingVirtualBackgroundDeleted,
  signalingVirtualBackgroundUploaded,
} from 'features/virtual-backgrounds/actions';

const defaultSelectedValue: SelectedVirtualBackground = {
  id: '',
  type: '',
  value: '',
  thumbnail: '',
};

export const initialState: VirtualBackgroundsState = {
  status: 'idle',
  failure: null,
  selected: {
    ...defaultSelectedValue,
  },
  enforce: false,
  syncConfig: undefined,
  backgrounds: defaultVirtualBackgrounds,
  vbProviderV2Enabled: false,
  persistenceEnabled: false,
};

type SelectionAction = PayloadAction<SelectedVirtualBackground, string, { sync?: boolean }>;

const prepareSelection = (payload: SelectedVirtualBackground, meta: { sync?: boolean } = {}) => ({
  payload,
  meta,
});

const recordSelection = (state: VirtualBackgroundsState, action: SelectionAction) => {
  state.selected = action.payload;

  if (action.meta.sync) {
    state.syncConfig = action.payload;
  }
};

const clearSelection = (state: VirtualBackgroundsState) => {
  state.selected = { ...defaultSelectedValue };
  state.syncConfig = undefined;
};

export const virtualBackgroundsSlice = createSlice({
  name: 'virtualBackground',
  initialState,
  reducers: {
    virtualBackgroundChangeStarted(state) {
      if (state.status !== 'ready') {
        return;
      }
      state.status = 'updating';
      state.failure = null;
    },
    virtualBackgroundReset: clearSelection,
    virtualBackgroundChangeFulfilled(state) {
      if (state.status === 'updating') {
        state.status = 'ready';
      }
    },
    virtualBackgroundChanged: {
      reducer: (state, action: SelectionAction) => {
        state.status = 'ready';
        state.failure = null;
        recordSelection(state, action);
      },
      prepare: prepareSelection,
    },
    // Record-only: a selection to apply at the next publish, without claiming the provider is ready.
    virtualBackgroundSelected: {
      reducer: recordSelection,
      prepare: prepareSelection,
    },
    virtualBackgroundActivationFailed(
      state,
      action: PayloadAction<VirtualBackgroundActivationFailure>
    ) {
      const { reason } = action.payload;

      if (reason === 'unsupported') {
        state.status = 'error';
      } else if (state.status === 'updating') {
        state.status = 'ready';
      }
      state.failure = reason;
      state.selected = { ...defaultSelectedValue };
    },
    // Unlike virtualBackgroundReset this does not trigger the reset saga (feed reset + SDK event).
    virtualBackgroundSelectionDropped: clearSelection,
    customBackgroundApplied(state, action: PayloadAction<SelectedVirtualBackground>) {
      state.customBackground = action.payload;
    },
    virtualBackgroundEnforceToggled(state, action: PayloadAction<boolean>) {
      state.enforce = action.payload;
    },
    syncConfigApplied(state, action: PayloadAction<VirtualBackgroundSyncConfig>) {
      state.syncConfig = action.payload;

      if (action.payload === false) {
        state.selected = {
          ...defaultSelectedValue,
        };
      }
    },
    customBackgroundAdded(state, action: PayloadAction<VirtualBackgroundItem>) {
      state.backgrounds.push(action.payload);
    },
    customBackgroundDeleted(state, action: PayloadAction<{ id: string; persisted?: boolean }>) {
      state.backgrounds = state.backgrounds.filter((bg) => bg.id !== action.payload.id);
    },
    customBackgroundUploadAccepted(state, action: PayloadAction<{ tempId: string; id: string }>) {
      const background = state.backgrounds.find((bg) => bg.id === action.payload.tempId);
      if (background) {
        background.id = action.payload.id;
        background.persisted = true;
      }
      if (state.selected.id === action.payload.tempId) {
        state.selected.id = action.payload.id;
      }
    },
    virtualBackgroundInitialized(state) {
      if (state.status === 'idle' || state.status === 'error') {
        state.status = 'ready';
        state.failure = null;
      }
    },
    virtualBackgroundCleared(state) {
      state.status = 'idle';
      state.failure = null;
      clearSelection(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initVirtualBackground.pending, (state) => {
        state.status = 'initializing';
      })
      .addCase(initVirtualBackground.fulfilled, (state) => {
        state.status = 'ready';
        state.failure = null;
      })
      .addCase(initVirtualBackground.rejected, (state, { meta, payload }) => {
        if (meta.condition) {
          return;
        }
        // null = the provider was destroyed mid-init (teardown), which is not a failure
        if (payload === null) {
          state.status = 'idle';
          state.failure = null;
          return;
        }
        state.status = 'error';
        state.failure = payload ?? 'failed';
      })
      .addCase(SDKConnected, (state, action) => {
        const options = action.payload;

        if (options.virtualBackgrounds) {
          state.backgrounds = mergeVirtualBackgrounds(
            options.replaceVirtualBackgrounds ? [] : state.backgrounds,
            options.virtualBackgrounds
          );
        }
      })
      .addCase(roomValidated, (state, action) => {
        state.vbProviderV2Enabled = action.payload.vbProviderV2Enabled;
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const persistedBackgrounds = action.payload.virtualBackgrounds;
        state.persistenceEnabled = persistedBackgrounds !== undefined;

        if (!persistedBackgrounds?.length) {
          return;
        }

        const existingIds = new Set(state.backgrounds.map((bg) => bg.id));
        const newItems = mapPersistedVirtualBackgrounds(persistedBackgrounds).filter(
          (item) => !existingIds.has(item.id)
        );
        state.backgrounds.push(...newItems);
      })
      .addCase(signalingVirtualBackgroundUploaded, (state, action) => {
        const background = state.backgrounds.find((bg) => bg.id === action.payload.id);
        if (background) {
          background.label = action.payload.name;
        }
      })
      .addCase(signalingVirtualBackgroundDeleted, (state, action) => {
        state.backgrounds = state.backgrounds.filter((bg) => bg.id !== action.payload.id);
      });
  },
});

export const {
  virtualBackgroundChanged,
  virtualBackgroundChangeStarted,
  virtualBackgroundSelected,
  virtualBackgroundSelectionDropped,
  virtualBackgroundActivationFailed,
  virtualBackgroundReset,
  virtualBackgroundChangeFulfilled,
  customBackgroundApplied,
  virtualBackgroundEnforceToggled,
  syncConfigApplied,
  customBackgroundAdded,
  customBackgroundDeleted,
  customBackgroundUploadAccepted,
  virtualBackgroundInitialized,
  virtualBackgroundCleared,
} = virtualBackgroundsSlice.actions;

export default virtualBackgroundsSlice.reducer;
